// Katalog der geraeteinternen Einstellungen - das, was am TV im Service-Menue
// (Samsung: Hotel-/Hospitality-Menue) bzw. im Pro:Centric-/Installer-Menue (LG)
// steht.
//
// Die Schluessel sind dieselben, die der SocketAdmin sendet und anzeigt
// (socketadmin/web/src/lib/deviceSettings.ts). Wer hier einen Schluessel
// aendert, muss ihn dort mitaendern - der Draht kennt keine Uebersetzung.
//
// WAS BELEGT IST UND WAS NICHT:
//
//  - Samsung `webapis.settings.getItemValue/setItemValue` ist belegt: die
//    OSD-Sprache laeuft in dieser App seit 2026 darueber (helpers/OsdLanguage,
//    Menue-Id 'osd-language', Samsung Partner Summit 2026 "TEP Web API's -
//    Settings"). Welche WEITEREN Menue-Ids eine Firmware kennt, ist nicht
//    dokumentiert - die Ids unten folgen der Schreibweise von 'osd-language'
//    und sind Kandidaten.
//  - Samsung Lautstaerke/Stumm laufen ueber `tizen.tvaudiocontrol` - das ist
//    dokumentierte Tizen-TV-API und der einzige Teil, der sicher funktioniert.
//    GEMESSEN auf einem BU800 (Tizen 6.x, 09/2026): von allen Schluesseln kamen
//    genau diese beiden zurueck - dieses Modell hat GAR KEIN webapis.settings,
//    und jeder Getter von webapis.systemcontrol antwortet mit "Fehler 9".
//    Mehr ist dort nicht zu holen. Auf einem U800F (Tizen 9) dagegen antworten
//    die Menue-Ids 'auto-power-off', 'screen-saver', 'brightness', 'contrast',
//    'sharpness', 'sound-mode' und 'game-mode' - mit Namen, Auswahl und
//    Wertebereich. Die systemcontrol-Getter liefern auch dort nichts, nur
//    still statt mit Fehlercode.
//
//    Sprache: 'osd-language' antwortet auf dem U800F NICHT, obwohl diese App
//    sie ueber genau diese Menue-Id setzt (helpers/OsdLanguage). Auch
//    'language', 'menu-language' und 'system-manager-language' bleiben stumm.
//    Die Menuesprache gehoert auf diesen Modellen nicht zu dem, was
//    webapis.settings herausgibt.
//  - Samsung B2B (`webapis.systemcontrol`): die Aufzaehlung nennt
//    getAutoPowerOn, getButtonLock, getIRLock, getSafetyLock, getPanelMute,
//    getMessageDisplay, getPCConnection, getScreenLampSchedule (je mit
//    set-Gegenstueck) - GEMESSEN antwortet davon auf BU800 und U800F kein
//    einziger: der BU800 mit "error code 9", der U800F stumm.
//
//    Neun ist NOT_SUPPORTED_ERR (Tizen benutzt die DOMException-Nummern; eine
//    Rechte-Ablehnung waere 18 gewesen, und das Privileg systemcontrol steht
//    in der config.xml). Die Firmware sagt also nicht "darfst du nicht",
//    sondern "gibt es hier nicht".
//
//    Daraus die Regel: auf Samsung sagt die API-Aufzaehlung NICHTS darueber,
//    was ein Modell kann - webapis.js ist eine generische Bibliothek und
//    listet ueberall dasselbe. Nur der Aufruf zaehlt. Auf LG ist es umgekehrt,
//    dort unterscheiden sich die hcap-Namensraeume je Modell wirklich.
//    Siehe docs/wgt-privilegien.md.
//  - LG: GEMESSEN auf 43UK660H und 43UM767H (09/2026).
//    hcap.property.getHotelMode liefert das KOMPLETTE Hotelmenue als
//    JSON-Dokument - Einschaltlautstaerke, Lautstaerkegrenzen, Einschaltquelle,
//    Tastensperren, Menue-Anzeige, alles. Der Katalog adressiert die Werte
//    ueber einen Pfad in dieses Dokument, geschrieben wird read-modify-write
//    (siehe LG.js). Tonausgabe und Menuesprache haben eigene Methoden
//    (hcap.system.getSoundOutput / getLocale). Unter getProperty liegt davon
//    nichts: jeder geratene Name kam mit "get_tv_property() failed" zurueck.
//
// Deshalb wird pro Einstellung eine LISTE von Kandidaten versucht und
// zurueckgemeldet, welcher geantwortet hat (`details`) und welche Schluessel
// das Geraet ueberhaupt kennt (`supported`). Der SocketAdmin graut aus, was
// nicht in `supported` steht - "diese Firmware kann das nicht" ist selbst eine
// Antwort und besser als eine leere Tabelle. Auf echter Hardware laesst sich
// die Liste damit Zeile fuer Zeile verifizieren, ohne die App anzufassen.

export const SETTING_TYPE = {
  SWITCH: 'switch',
  NUMBER: 'number',
  TEXT: 'text',
};

/**
 * Ein Eintrag:
 *   key    - Schluessel auf dem Draht (identisch mit dem SocketAdmin)
 *   type   - switch | number | text (bestimmt die Umwandlung, siehe index.js)
 *   tizen  - { setting: [Menue-Ids], b2b: [Methoden-Staemme], audio: 'volume'|'mute',
 *              writeOnly: true }
 *            `writeOnly` heisst: setzbar, auch wenn das Geraet keinen Ist-Wert
 *            herausgibt. Nur fuer Wege, die der Hersteller dokumentiert.
 *            b2b-Staemme werden zu get<Stamm>/set<Stamm> auf
 *            webapis.systemcontrol bzw. b2bapis.b2bcontrol. Aufgerufen wird
 *            nur, was das Objekt wirklich hat - geraten ist hier gefahrlos.
 *   lg     - { hotel: 'pfad', enablePath: 'pfad', values: {…} }
 *              der Regelfall: ein Pfad in das Dokument, das
 *              hcap.property.getHotelMode liefert (das komplette Hotelmenue).
 *              `enablePath` ist der Schalter des Blocks, den ein Wert braucht,
 *              um zu wirken; `values` bildet das Vokabular des Admins auf das
 *              des Geraets ab ('standby' -> 'stand_by').
 *            { method: {ns, get, set, field, setParam, values, locale} }
 *              fuer die Werte mit eigener Methode (Tonausgabe, Menuesprache).
 *              `field` sagt, in welchem Feld der Antwort der Wert steht,
 *              `values` bildet das Vokabular ab, `locale` rechnet zwischen
 *              Sprachkuerzel und BCP-47-Kennung um.
 *            { property: [Namen] }
 *              Rueckfall ueber getProperty. Bleibt leer: auf 43UK660H und
 *              43UM767H hat kein einziger geratener Name geantwortet
 *              ("get_tv_property() failed").
 * Fehlt der Plattform-Block, gibt es fuer diese Plattform keinen Weg - der
 * Schluessel taucht dann gar nicht erst in der Antwort auf.
 */
export const DEVICE_SETTINGS = [
  // --- Ton: der einzige Block, der auf beiden Plattformen ueber eine
  //     dokumentierte API laeuft (Samsung tvaudiocontrol). --------------------
  {
    key: 'volume',
    type: SETTING_TYPE.NUMBER,
    tizen: { audio: 'volume', b2b: ['Volume'] },
  },
  {
    key: 'mute',
    type: SETTING_TYPE.SWITCH,
    tizen: { audio: 'mute', b2b: ['Mute'] },
  },

  // --- Einschaltverhalten --------------------------------------------------
  {
    key: 'power_on_state',
    type: SETTING_TYPE.TEXT,
    tizen: { setting: ['power-on-state', 'power-state'], b2b: ['PowerOnState', 'PowerOnDefault'] },
    lg: {
      hotel: 'settings.powerOnStatus',
      // 'stand_by' ist gemessen; die beiden anderen sind die naheliegenden
      // Gegenstuecke und noch nicht belegt. Ein Fehlgriff faellt beim
      // Rueckvergleich nach dem Setzen auf.
      values: { standby: 'stand_by', on: 'power_on', last: 'last_status' },
    },
  },
  {
    key: 'power_on_source',
    type: SETTING_TYPE.TEXT,
    tizen: { setting: ['power-on-source', 'startup-source'], b2b: ['PowerOnSource', 'StartupSource', 'DefaultSource'] },
    lg: { hotel: 'settings.powerOnDefault.settings.input', enablePath: 'settings.powerOnDefault.enable' },
  },
  {
    key: 'power_on_channel',
    type: SETTING_TYPE.TEXT,
    tizen: { setting: ['power-on-channel'], b2b: ['PowerOnChannel', 'StartupChannel', 'DefaultChannel'] },
    lg: { hotel: 'settings.powerOnDefault.settings.programme', enablePath: 'settings.powerOnDefault.enable' },
  },
  {
    key: 'power_on_volume',
    type: SETTING_TYPE.NUMBER,
    tizen: { setting: ['power-on-volume', 'startup-volume'], b2b: ['PowerOnVolume', 'StartupVolume', 'DefaultVolume'] },
    lg: {
      hotel: 'settings.volume.settings.startVolume.level',
      enablePath: 'settings.volume.settings.startVolume.enable',
    },
  },
  {
    key: 'power_on_mute',
    type: SETTING_TYPE.SWITCH,
    tizen: { setting: ['power-on-mute'], b2b: ['PowerOnMute', 'StartupMute'] },
  },
  {
    // GEMESSEN: webapis.systemcontrol.getAutoPowerOn/setAutoPowerOn.
    key: 'auto_power_on',
    type: SETTING_TYPE.SWITCH,
    tizen: { b2b: ['AutoPowerOn'] },
  },
  {
    key: 'instant_on',
    type: SETTING_TYPE.SWITCH,
    tizen: { setting: ['instant-on', 'fast-start'], b2b: ['InstantOn', 'FastStart', 'StandbyMode'] },
  },

  // --- Lautstaerke-Grenzen -------------------------------------------------
  {
    key: 'max_volume',
    type: SETTING_TYPE.NUMBER,
    tizen: { setting: ['max-volume', 'maximum-volume'], b2b: ['MaxVolume', 'MaximumVolume', 'VolumeMax'] },
    lg: { hotel: 'settings.volume.settings.maximumVolume', enablePath: 'settings.volume.enable' },
  },
  {
    key: 'min_volume',
    type: SETTING_TYPE.NUMBER,
    tizen: { b2b: ['MinVolume', 'MinimumVolume'] },
    lg: { hotel: 'settings.volume.settings.minimumVolume', enablePath: 'settings.volume.enable' },
  },
  {
    key: 'sound_output',
    type: SETTING_TYPE.TEXT,
    tizen: { setting: ['sound-output'], b2b: ['SoundOutput', 'Speaker'] },
    lg: {
      method: {
        ns: 'system',
        get: 'getSoundOutput',
        set: 'setSoundOutput',
        field: 'soundOutput',
        // LG antwortet mit einer Zahl. 0 ist gemessen (Geraet gibt Ton ueber
        // die eigenen Lautsprecher aus); die uebrigen Nummern sind nicht
        // belegt und bleiben deshalb ohne Zuordnung - wer sie setzt, bekommt
        // eine Absage statt eines stummen Fernsehers.
        values: { tv_speaker: 0 },
      },
    },
  },

  // --- Bild & Energie ------------------------------------------------------
  {
    key: 'energy_saving',
    type: SETTING_TYPE.TEXT,
    tizen: { setting: ['energy-saving', 'eco-solution'], b2b: ['EnergySaving', 'EcoSolution'] },
  },
  {
    key: 'backlight',
    type: SETTING_TYPE.NUMBER,
    tizen: { setting: ['backlight', 'picture-backlight'], b2b: ['Backlight', 'PanelBacklight'] },
  },
  {
    key: 'no_signal_power_off',
    type: SETTING_TYPE.TEXT,
    tizen: { setting: ['auto-power-off', 'no-signal-power-off'], b2b: ['AutoPowerOff', 'NoSignalPowerOff'] },
  },
  {
    key: 'screen_saver',
    type: SETTING_TYPE.SWITCH,
    tizen: { setting: ['screen-saver'], b2b: ['ScreenSaver'] },
  },

  // --- Bedienung & Sperren -------------------------------------------------
  {
    key: 'key_lock',
    type: SETTING_TYPE.SWITCH,
    tizen: { setting: ['panel-button-lock', 'panel-key-lock'], b2b: ['ButtonLock'] },
    lg: {
      hotel: 'settings.keyManagement.settings.localKeyOperation',
      enablePath: 'settings.keyManagement.enable',
      // 'normal' ist gemessen, 'blocked' das naheliegende Gegenstueck.
      values: { off: 'normal', on: 'blocked' },
    },
  },
  {
    // HEIKEL: gesperrt nimmt das Geraet keine Fernbedienung mehr an. Der
    // SocketAdmin fragt davor nach; hier gibt es keine Sonderbehandlung - die
    // App fuehrt aus, was der Admin schickt.
    key: 'ir_lock',
    type: SETTING_TYPE.SWITCH,
    tizen: { setting: ['remocon-lock', 'remote-lock'], b2b: ['IRLock'] },
    lg: {
      hotel: 'settings.keyManagement.settings.irOperation',
      enablePath: 'settings.keyManagement.enable',
      values: { off: 'normal', on: 'blocked' },
    },
  },
  {
    key: 'menu_display',
    type: SETTING_TYPE.SWITCH,
    tizen: { setting: ['menu-display'], b2b: ['MenuDisplay'] },
    lg: { hotel: 'settings.limitedMode.settings.menuDisplay', enablePath: 'settings.limitedMode.enable' },
  },
  {
    key: 'home_menu_display',
    type: SETTING_TYPE.SWITCH,
    tizen: { setting: ['home-menu-display'], b2b: ['HomeMenuDisplay'] },
  },
  {
    key: 'usb_access',
    type: SETTING_TYPE.SWITCH,
    tizen: { setting: ['usb-access', 'media-play'], b2b: ['UsbAccess', 'MediaPlay'] },
  },
  {
    key: 'bluetooth',
    type: SETTING_TYPE.SWITCH,
    tizen: { setting: ['bluetooth-audio', 'bluetooth'], b2b: ['BluetoothAudio', 'Bluetooth'] },
  },
  {
    key: 'channel_edit_lock',
    type: SETTING_TYPE.SWITCH,
    tizen: { setting: ['channel-edit-lock'], b2b: ['ChannelEditLock'] },
  },

  {
    // GEMESSEN: webapis.systemcontrol.getPanelMute/setPanelMute. HEIKEL - ein
    // stummgeschaltetes Panel sieht aus wie ein defektes Geraet.
    key: 'panel_mute',
    type: SETTING_TYPE.SWITCH,
    tizen: { b2b: ['PanelMute'] },
  },
  {
    // GEMESSEN: webapis.systemcontrol.getSafetyLock/setSafetyLock.
    key: 'safety_lock',
    type: SETTING_TYPE.SWITCH,
    tizen: { b2b: ['SafetyLock'] },
  },
  {
    // GEMESSEN: webapis.systemcontrol.getMessageDisplay/setMessageDisplay.
    key: 'message_display',
    type: SETTING_TYPE.SWITCH,
    tizen: { b2b: ['MessageDisplay'] },
  },

  // --- Betriebsart ---------------------------------------------------------
  {
    // HEIKEL: aus faellt das Geraet auf den normalen Endkunden-TV zurueck.
    key: 'hotel_mode',
    type: SETTING_TYPE.SWITCH,
    tizen: { setting: ['hotel-mode', 'hospitality-mode'], b2b: ['HotelMode', 'HospitalityMode'] },
    lg: { hotel: 'enable' },
  },
  {
    // Menuesprache des GERAETS, nicht die der App. Auf Samsung ist das die
    // belegte Menue-Id - und der Wert ist eine Locale ('de_DE.utf8'), waehrend
    // der Admin ein Sprachkuerzel ('de') schickt. Die Umrechnung macht der
    // Tizen-Adapter ueber OSD_LOCALE, damit auf dem Draht ueberall dasselbe
    // Kuerzel steht wie bei set_language.
    key: 'osd_language',
    type: SETTING_TYPE.TEXT,
    tizen: {
      setting: ['osd-language'],
      locale: true,
      // Samsung dokumentiert setItemValue('osd-language','fr_FR.utf8') als DEN
      // Weg, die Menuesprache zu setzen; getItemValue derselben Id antwortet
      // auf einem U800F aber nicht. Deshalb schreibbar ohne Lesewert - siehe
      // Tizen.js.
      //
      // GEKLAERT am 04.09.2026 an einem U800F ueber den SocketAdmin. Die
      // Nachfrage bei der Id liefert:
      //
      //   name    'Sprache'
      //   path    mainmenu > system > osd-language
      //   visible false
      //   enabled false
      //   getItemValue    -> "Cannot read properties of undefined
      //                       (reading 'itemdata')"
      //   getItemDataList -> nichts
      //
      // Die Firmware KENNT den Punkt also - Name und Pfad stehen da -, hat
      // aber keine Item-Daten dazu. Der Fehler ist ein TypeError INNERHALB von
      // webapis.js, kein Fehlercode des Geraets: die Bibliothek greift auf
      // `.itemdata` von undefined zu. Deshalb liefern weder getItemValue noch
      // getItemDataList etwas, und deshalb gibt es auch keine Sprachliste vom
      // Geraet.
      //
      // Am Zustand liegt es nicht: brightness, contrast und game-mode stehen
      // ebenfalls auf enabled:false und lesen sich einwandfrei. osd-language
      // ist ein Sonderfall.
      //
      // FAZIT: auf diesem Modell ist die Menuesprache NUR SETZBAR, und zwar
      // ueber genau diesen Weg - setItemValue meldet true, das Geraet bleibt
      // ruhig. writeOnly ist damit nicht ein Behelf, sondern die richtige
      // Beschreibung.
      writeOnly: true,
      b2b: ['OsdLanguage', 'MenuLanguage'],
    },
    lg: {
      method: {
        ns: 'system',
        get: 'getLocale',
        // getLocale antwortet {country:'United Kingdom', language:'English',
        // specifier:'en-GB'} - die Kennung steht in `specifier`. `locale`
        // rechnet zwischen dem Sprachkuerzel des Admins ('de') und der
        // BCP-47-Kennung des Geraets ('de-DE') um.
        field: 'specifier',
        locale: true,
        set: 'requestLocaleChange',
        setParam: 'specifier',
      },
    },
  },
  {
    // App-Daten beim naechsten Neustart loeschen (Samsung).
    //
    // `deleteAppPrivateInfo('ON')` merkt das Loeschen VOR - Cast-Anmeldungen,
    // LocalStorage, Browserdaten der vorinstallierten Apps verschwinden beim
    // naechsten Hochfahren. Das ist der dokumentierte Weg des Partner-
    // Trainings, und er ist waehrend eines Gastaufenthalts gefahrlos zu
    // setzen, weil er erst beim Neustart greift.
    //
    // GEMESSEN am 04.09.2026 an einem U800F: kurz nach dem Setzen ging das
    // Geraet AUS und meldete sich nicht wieder am Socket-Server. "Beim
    // naechsten Neustart" heisst auf dieser Firmware offenbar: sie startet
    // gleich neu - und bleibt danach im Standby, wenn der Einschaltzustand so
    // steht. Die Registrierung der App ueberlebt das: sie wird beim Start
    // ueber den Fingerprint (Seriennummer, MAC, Modell) wiedergefunden, auch
    // wenn der lokale Speicher mit geloescht wurde.
    //
    // Deshalb im Admin als heikel gekennzeichnet: Rueckfrage vor dem Setzen,
    // und ein eigenes Kommando nach allen harmlosen - sonst ist hinterher
    // nicht zu sagen, was der Neustart mitgenommen hat.
    //
    // WEITER OFFEN: ob das Kennzeichen STEHEN BLEIBT und damit bei jedem
    // Neustart erneut loescht, oder ob die Firmware es nach dem Loeschen
    // zuruecksetzt. Einen Getter gibt es nicht, der Ist-Wert ist also nie zu
    // sehen. Nachweisbar nur so: setzen, Geraet wieder einschalten, ein
    // zweites Mal neu starten und pruefen, ob die Cast-Anmeldung erneut weg
    // ist. Bleibt es nicht stehen, gehoert das Setzen in den
    // Check-out-Ablauf - dort steht es ueber armClearCredentials schon.
    key: 'clear_credentials_boot',
    type: SETTING_TYPE.SWITCH,
    tizen: {
      call: { method: 'deleteAppPrivateInfo', on: 'ON', off: 'OFF' },
      writeOnly: true,
    },
  },
];

// Beschriftungen fuer die Info-Seite. Dieselben Texte benutzt der SocketAdmin
// in seiner Tabelle - wer hier umbenennt, sollte es dort mitziehen, sonst heisst
// dieselbe Zeile am Geraet anders als in der Ferne.
export const SETTING_LABELS = {
  volume: 'Lautstärke',
  mute: 'Stumm',
  power_on_state: 'Zustand nach Netz-Ein',
  power_on_source: 'Quelle beim Einschalten',
  power_on_channel: 'Programmplatz beim Einschalten',
  power_on_volume: 'Einschaltlautstärke',
  power_on_mute: 'Stumm beim Einschalten',
  instant_on: 'Schnellstart (Instant On)',
  max_volume: 'Maximale Lautstärke',
  min_volume: 'Minimale Lautstärke',
  sound_output: 'Tonausgabe',
  energy_saving: 'Energiesparmodus',
  backlight: 'Hintergrundbeleuchtung',
  no_signal_power_off: 'Abschalten ohne Signal',
  screen_saver: 'Bildschirmschoner',
  key_lock: 'Gerätetasten gesperrt',
  ir_lock: 'Fernbedienung gesperrt',
  menu_display: 'Gerätemenü erreichbar',
  home_menu_display: 'Home-Taste erreichbar',
  usb_access: 'USB-Wiedergabe',
  bluetooth: 'Bluetooth-Audio',
  channel_edit_lock: 'Programmliste gesperrt',
  hotel_mode: 'Hotel-Modus',
  osd_language: 'Menüsprache des Geräts',
  auto_power_on: 'Einschalten bei Netz-Ein',
  panel_mute: 'Bildschirm abgeschaltet (Panel)',
  safety_lock: 'Sicherheitssperre',
  message_display: 'Meldungen einblenden',
  clear_credentials_boot: 'App-Daten beim nächsten Neustart löschen',
};

/** Ein Katalogeintrag oder undefined - unbekannte Schluessel gehen nicht raus. */
export function settingByKey(key) {
  for (let i = 0; i < DEVICE_SETTINGS.length; i += 1) {
    if (DEVICE_SETTINGS[i].key === key) return DEVICE_SETTINGS[i];
  }
  return undefined;
}



// WEBPACK FOOTER //
// ./src/helpers/DeviceSettings/catalog.js